/**
 * 後台角色的**顯示名**（批97 Bug 4）。
 *
 * ★★ 這是「畫面上的角色中文名」的**唯一真相**。批97 之前只有 `AdminUsersView` 有一支
 *    區域 `roleLabel()`，其餘 12 處（開通政策標籤、錯誤文案、告警摘要、唯讀橫幅、
 *    直發 title…）直接把 `superadmin`／`admin` 印給人看——對非工程同事等於天書。
 *    新增角色或改中文名一律改這裡，view 不得再各自寫查表。
 *
 * 後端有**另一份** `backend/app/core/security.py::ROLE_LABELS`（稽核 summary 用）。
 * 兩份是刻意的（後端不吃前端的 bundle），**改一邊要改兩邊**。
 *
 * ⚠️ 這裡純粹是**文案**，不是權限判斷。可見性一律走 `stores/auth.ts` 的 `ROLE_RANK`／
 *    `meta.roles`（`reseller` 刻意不在 rank 內），真正的閘門在後端。
 */
import type { RoleName } from '@/stores/auth'

/** 角色代號 → 中文名。鍵集合＝`STAFF_ROLES` ＋ `reseller`（由 `roles.spec.ts` 機械釘住）。 */
export const ROLE_LABELS: Record<string, string> = {
  superadmin: '超級管理者',
  manager: '營運主管',
  admin: '管理員',
  operator: '維運人員',
  reseller: '代理商',
}

/**
 * 角色中文名；查不到回**原字串**（後端上了新角色而前端還沒跟上時，寧可印英文代號也不要
 * 印空白），`null`／`undefined`／空字串回 `—`。
 */
export function roleLabel(role: string | null | undefined): string {
  if (!role) return '—'
  return ROLE_LABELS[role] ?? role
}

export interface RoleOption {
  value: RoleName
  label: string
}

/**
 * 我方員工四級，順序**低→高**（與 `STAFF_ROLES` 逐項相同，spec 有斷言）。
 * 角色下拉在「同族之內」才給換——staff ↔ reseller 互轉後端 422。
 */
export const STAFF_ROLE_OPTIONS: RoleOption[] = [
  { value: 'operator', label: ROLE_LABELS.operator },
  { value: 'admin', label: ROLE_LABELS.admin },
  { value: 'manager', label: ROLE_LABELS.manager },
  { value: 'superadmin', label: ROLE_LABELS.superadmin },
]

/** 四個角色（新增帳號的下拉；代理商排在我方三級之後）。 */
export const ROLE_OPTIONS: RoleOption[] = [
  ...STAFF_ROLE_OPTIONS,
  { value: 'reseller', label: ROLE_LABELS.reseller },
]
